import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Lock, LogOut, Image, Calendar, Users, FileText, 
  Plus, Trash2, Edit, Save, X, Eye, Settings,
  Megaphone, DollarSign, RefreshCw, Check, AlertCircle, Home
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('gallery');

  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password,
      });

      if (response.data.token) {
        setToken(response.data.token);
        localStorage.setItem('admin_token', response.data.token);
        setIsAuthenticated(true);
        toast.success('Login successful!');
      }
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setToken('');
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    toast.info('Logged out');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-background">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md p-8 rounded-2xl bg-card border border-border"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold font-['Outfit'] text-foreground">Admin Panel</h1>
            <p className="text-muted-foreground text-sm mt-2">TNMC Content Management</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Username</label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
                data-testid="admin-username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                data-testid="admin-password"
              />
            </div>
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full"
              data-testid="admin-login-btn"
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  const tabs = [
    { id: 'homepage', label: 'Homepage', icon: Home },
    { id: 'gallery', label: 'Gallery', icon: Image },
    { id: 'flyers', label: 'Flyers', icon: Megaphone },
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'board', label: 'Board', icon: Users },
    { id: 'fundraising', label: 'Fundraising', icon: DollarSign },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen pt-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold font-['Outfit'] text-foreground">Admin Panel</h1>
            <p className="text-muted-foreground">Manage TNMC website content</p>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-btn">
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 p-2 rounded-xl bg-card border border-border">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-muted-foreground'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="bg-card rounded-2xl border border-border p-6">
          {activeTab === 'homepage' && <HomepageManager token={token} />}
          {activeTab === 'gallery' && <GalleryManager token={token} />}
          {activeTab === 'flyers' && <FlyersManager token={token} />}
          {activeTab === 'events' && <EventsManager token={token} />}
          {activeTab === 'board' && <BoardManager token={token} />}
          {activeTab === 'fundraising' && <FundraisingManager token={token} />}
          {activeTab === 'settings' && <SettingsManager token={token} />}
        </div>
      </div>
    </div>
  );
};

// Gallery Manager with full CRUD
const GalleryManager = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    location: '',
    images: [{ url: '', alt: '', caption: '' }],
  });

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/gallery/events`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to fetch gallery events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const resetForm = () => {
    setFormData({ title: '', description: '', date: '', location: '', images: [{ url: '', alt: '', caption: '' }] });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (event) => {
    setFormData({
      title: event.title || '',
      description: event.description || '',
      date: event.date || '',
      location: event.location || '',
      images: event.images?.length ? event.images : [{ url: '', alt: '', caption: '' }],
    });
    setEditingId(event.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/gallery/events/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Gallery event updated!');
      } else {
        await axios.post(`${API}/gallery/events`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Gallery event added!');
      }
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error('Failed to save gallery event');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await axios.delete(`${API}/gallery/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const addImageField = () => {
    setFormData({
      ...formData,
      images: [...formData.images, { url: '', alt: '', caption: '' }],
    });
  };

  const updateImage = (index, field, value) => {
    const newImages = [...formData.images];
    newImages[index][field] = value;
    setFormData({ ...formData, images: newImages });
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages.length ? newImages : [{ url: '', alt: '', caption: '' }] });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Gallery Events ({events.length})</h2>
        <Button onClick={() => { resetForm(); setShowForm(!showForm); }}>
          {showForm ? <X className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
          {showForm ? 'Cancel' : 'Add Event'}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-8 p-6 rounded-xl bg-background border border-border space-y-4">
          <h3 className="font-semibold text-foreground mb-4">{editingId ? 'Edit Event' : 'Add New Event'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title *</label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Date *</label>
              <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Location *</label>
            <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full px-4 py-2 rounded-xl bg-card border border-border text-foreground" rows={3} />
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">Images</label>
              <Button type="button" variant="outline" size="sm" onClick={addImageField}>
                <Plus className="h-3 w-3 mr-1" /> Add Image
              </Button>
            </div>
            {formData.images.map((img, index) => (
              <div key={index} className="p-3 mb-2 rounded-lg bg-muted/30 border border-border">
                <div className="flex gap-2 mb-2">
                  <Input placeholder="Image URL *" value={img.url} onChange={(e) => updateImage(index, 'url', e.target.value)} className="flex-1" />
                  {formData.images.length > 1 && (
                    <Button type="button" variant="destructive" size="icon" onClick={() => removeImage(index)}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input placeholder="Alt text" value={img.alt} onChange={(e) => updateImage(index, 'alt', e.target.value)} className="flex-1" />
                  <Input placeholder="Caption" value={img.caption} onChange={(e) => updateImage(index, 'caption', e.target.value)} className="flex-1" />
                </div>
                {img.url && (
                  <div className="mt-2">
                    <img src={img.url} alt={img.alt || 'Preview'} className="h-20 w-auto rounded object-cover" onError={(e) => e.target.style.display = 'none'} />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <Button type="submit" className="flex-1">
              <Save className="h-4 w-4 mr-2" /> {editingId ? 'Update Event' : 'Save Event'}
            </Button>
            <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <div key={event.id} className="p-4 rounded-xl bg-background border border-border">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground">{event.title}</h3>
                  <p className="text-sm text-muted-foreground">{event.date} • {event.location}</p>
                  <p className="text-sm text-muted-foreground mt-1">{event.images?.length || 0} images</p>
                  {event.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{event.description}</p>}
                </div>
                <div className="flex gap-2 ml-4">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(event)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(event.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {event.images?.length > 0 && (
                <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                  {event.images.slice(0, 5).map((img, i) => (
                    <img key={i} src={img.url} alt={img.alt || ''} className="h-16 w-16 rounded object-cover flex-shrink-0" onError={(e) => e.target.style.display = 'none'} />
                  ))}
                  {event.images.length > 5 && <span className="text-sm text-muted-foreground self-center">+{event.images.length - 5} more</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Flyers Manager
// ============ Generic CRUD helpers ============

const EVENT_CATEGORIES = ['JUMMAH', 'RAMADAN', 'IFTAR', 'COMMUNITY', 'EID', 'EDUCATION'];
const BOARD_ROLES = ['Executive Board', 'Religious Leadership', 'Coordinators', 'Support'];

// ============ Events Manager (Full CRUD) ============
const EventsManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ category: 'JUMMAH', title: '', description: '', date: '', time: '', location: '54 St. Paul Crescent, Barrie' });
  const [filterCat, setFilterCat] = useState('ALL');

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/content/events`);
      setItems(res.data || []);
    } catch { toast.error('Failed to fetch events'); }
    setLoading(false);
  };

  useEffect(() => { fetchItems(); }, []);

  const reset = () => { setForm({ category: 'JUMMAH', title: '', description: '', date: '', time: '', location: '54 St. Paul Crescent, Barrie' }); setEditingId(null); setShowForm(false); };

  const handleEdit = (item) => {
    setForm({ category: item.category, title: item.title, description: item.description, date: item.date, time: item.time, location: item.location });
    setEditingId(item.id); setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      if (editingId) {
        await axios.put(`${API}/content/events/${editingId}`, form, cfg);
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/content/events`, form, cfg);
        toast.success('Event added');
      }
      reset(); fetchItems();
    } catch { toast.error('Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API}/content/events/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted'); fetchItems();
    } catch { toast.error('Delete failed'); }
  };

  const filtered = filterCat === 'ALL' ? items : items.filter(i => i.category === filterCat);
  const sorted = [...filtered].sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-xl font-semibold text-foreground">Events ({items.length})</h2>
        <div className="flex items-center gap-2">
          <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="px-3 py-2 rounded-lg bg-background border border-border text-sm" data-testid="event-filter">
            <option value="ALL">All categories</option>
            {EVENT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <Button onClick={() => { setShowForm(!showForm); setEditingId(null); }} data-testid="event-add-btn">
            <Plus className="h-4 w-4 mr-2" />{showForm ? 'Close' : 'Add Event'}
          </Button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="p-6 rounded-xl bg-background border border-border mb-6 space-y-3" data-testid="event-form">
          <h3 className="font-semibold text-foreground mb-2">{editingId ? 'Edit Event' : 'New Event'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="px-3 py-2 rounded-lg bg-card border border-border" required data-testid="event-category">
              {EVENT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="event-date" />
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="event-title" />
            <Input placeholder="Time (e.g. 1:30 PM)" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required data-testid="event-time" />
          </div>
          <Input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required data-testid="event-location" />
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={3} className="w-full px-3 py-2 rounded-lg bg-card border border-border" data-testid="event-description" />
          <div className="flex gap-2">
            <Button type="submit" data-testid="event-save-btn"><Save className="h-4 w-4 mr-2" />Save</Button>
            <Button type="button" variant="outline" onClick={reset}><X className="h-4 w-4 mr-2" />Cancel</Button>
          </div>
        </form>
      )}

      {loading ? <p className="text-muted-foreground">Loading…</p> : (
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {sorted.map((item) => (
            <div key={item.id} className="flex items-center justify-between p-3 rounded-lg bg-background border border-border" data-testid={`event-row-${item.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary">{item.category}</span>
                  <span className="text-xs text-muted-foreground">{item.date} • {item.time}</span>
                </div>
                <p className="font-medium text-foreground truncate">{item.title}</p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => handleEdit(item)} data-testid={`event-edit-${item.id}`}><Edit className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} className="text-red-400" data-testid={`event-delete-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
          {sorted.length === 0 && <p className="text-muted-foreground text-center py-8">No events found.</p>}
        </div>
      )}
    </div>
  );
};

// ============ Flyers Manager (Full CRUD) ============
const FlyersManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', date: '', time: '', location: '', image: '', category: 'Community Event', featured: false });

  const fetchItems = async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/content/flyers`); setItems(res.data || []); }
    catch { toast.error('Failed to fetch flyers'); }
    setLoading(false);
  };
  useEffect(() => { fetchItems(); }, []);

  const reset = () => { setForm({ title: '', description: '', date: '', time: '', location: '', image: '', category: 'Community Event', featured: false }); setEditingId(null); setShowForm(false); };

  const handleEdit = (item) => {
    setForm({ title: item.title, description: item.description, date: item.date, time: item.time, location: item.location, image: item.image, category: item.category, featured: !!item.featured });
    setEditingId(item.id); setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      if (editingId) { await axios.put(`${API}/content/flyers/${editingId}`, form, cfg); toast.success('Flyer updated'); }
      else { await axios.post(`${API}/content/flyers`, form, cfg); toast.success('Flyer added'); }
      reset(); fetchItems();
    } catch { toast.error('Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this flyer?')) return;
    try { await axios.delete(`${API}/content/flyers/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Deleted'); fetchItems(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-foreground">Flyers ({items.length})</h2>
        <Button onClick={() => { setShowForm(!showForm); setEditingId(null); }} data-testid="flyer-add-btn">
          <Plus className="h-4 w-4 mr-2" />{showForm ? 'Close' : 'Add Flyer'}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="p-6 rounded-xl bg-background border border-border mb-6 space-y-3" data-testid="flyer-form">
          <h3 className="font-semibold text-foreground mb-2">{editingId ? 'Edit Flyer' : 'New Flyer'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="flyer-title" />
            <Input placeholder="Category (e.g. Eid Prayer)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required data-testid="flyer-category" />
            <Input placeholder="Date (any format)" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="flyer-date" />
            <Input placeholder="Time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required data-testid="flyer-time" />
          </div>
          <Input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required data-testid="flyer-location" />
          <Input placeholder="Image URL (https://...)" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} required data-testid="flyer-image" />
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={3} className="w-full px-3 py-2 rounded-lg bg-card border border-border" data-testid="flyer-description" />
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="flyer-featured" />
            Featured (highlighted at top of Flyers page)
          </label>
          <div className="flex gap-2">
            <Button type="submit" data-testid="flyer-save-btn"><Save className="h-4 w-4 mr-2" />Save</Button>
            <Button type="button" variant="outline" onClick={reset}><X className="h-4 w-4 mr-2" />Cancel</Button>
          </div>
        </form>
      )}

      {loading ? <p className="text-muted-foreground">Loading…</p> : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} className="flex items-center gap-4 p-3 rounded-lg bg-background border border-border" data-testid={`flyer-row-${item.id}`}>
              <img src={item.image} alt={item.title} className="w-16 h-16 object-cover rounded" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary">{item.category}</span>
                  {item.featured && <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-500">FEATURED</span>}
                </div>
                <p className="font-medium text-foreground truncate">{item.title}</p>
                <p className="text-xs text-muted-foreground truncate">{item.date} • {item.location}</p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => handleEdit(item)} data-testid={`flyer-edit-${item.id}`}><Edit className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} className="text-red-400" data-testid={`flyer-delete-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-muted-foreground text-center py-8">No flyers yet.</p>}
        </div>
      )}
    </div>
  );
};

// ============ Board Manager (Full CRUD) ============
const BoardManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', title: '', role: 'Executive Board' });

  const fetchItems = async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/content/board`); setItems(res.data || []); }
    catch { toast.error('Failed to fetch board'); }
    setLoading(false);
  };
  useEffect(() => { fetchItems(); }, []);

  const reset = () => { setForm({ name: '', title: '', role: 'Executive Board' }); setEditingId(null); setShowForm(false); };

  const handleEdit = (item) => { setForm({ name: item.name, title: item.title, role: item.role }); setEditingId(item.id); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      if (editingId) { await axios.put(`${API}/content/board/${editingId}`, form, cfg); toast.success('Member updated'); }
      else { await axios.post(`${API}/content/board`, form, cfg); toast.success('Member added'); }
      reset(); fetchItems();
    } catch { toast.error('Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this member?')) return;
    try { await axios.delete(`${API}/content/board/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Removed'); fetchItems(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-foreground">Board Members ({items.length})</h2>
        <Button onClick={() => { setShowForm(!showForm); setEditingId(null); }} data-testid="board-add-btn">
          <Plus className="h-4 w-4 mr-2" />{showForm ? 'Close' : 'Add Member'}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="p-6 rounded-xl bg-background border border-border mb-6 space-y-3" data-testid="board-form">
          <h3 className="font-semibold text-foreground mb-2">{editingId ? 'Edit Member' : 'New Member'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="board-name" />
            <Input placeholder="Title (e.g. President)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="board-title" />
          </div>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-card border border-border" required data-testid="board-role">
            {BOARD_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <div className="flex gap-2">
            <Button type="submit" data-testid="board-save-btn"><Save className="h-4 w-4 mr-2" />Save</Button>
            <Button type="button" variant="outline" onClick={reset}><X className="h-4 w-4 mr-2" />Cancel</Button>
          </div>
        </form>
      )}

      {loading ? <p className="text-muted-foreground">Loading…</p> : (
        <div className="space-y-2">
          {BOARD_ROLES.map(role => {
            const group = items.filter(i => i.role === role);
            if (group.length === 0) return null;
            return (
              <div key={role}>
                <h4 className="text-sm font-medium text-primary mt-4 mb-2">{role}</h4>
                {group.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 rounded-lg bg-background border border-border mb-2" data-testid={`board-row-${item.id}`}>
                    <div>
                      <p className="font-medium text-foreground">{item.name}</p>
                      <p className="text-xs text-muted-foreground">{item.title}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(item)} data-testid={`board-edit-${item.id}`}><Edit className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} className="text-red-400" data-testid={`board-delete-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
          {items.length === 0 && <p className="text-muted-foreground text-center py-8">No members yet.</p>}
        </div>
      )}
    </div>
  );
};

// ============ Homepage Manager ============
const HomepageManager = ({ token }) => {
  const [form, setForm] = useState({ hero_badge: '', hero_title: '', mission_text: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content/homepage`)
      .then((res) => { if (res.data) setForm((p) => ({ ...p, ...res.data })); })
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/content/homepage`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Homepage updated — refresh / to view changes');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  if (loading) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div>
      <h2 className="text-xl font-semibold text-foreground mb-4">Homepage Content</h2>
      <p className="text-sm text-muted-foreground mb-6">Edit the hero section text shown on the homepage. Use <code className="px-1 py-0.5 rounded bg-muted text-xs">|</code> in the title to force a line break.</p>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-3xl" data-testid="homepage-form">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Hero Badge</label>
          <Input value={form.hero_badge} onChange={(e) => setForm({ ...form, hero_badge: e.target.value })} placeholder="e.g. Strengthening Faith. Serving Community." data-testid="homepage-badge" />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Hero Title</label>
          <Input value={form.hero_title} onChange={(e) => setForm({ ...form, hero_title: e.target.value })} placeholder="e.g. True North | Muslim Community" data-testid="homepage-title" />
          <p className="text-xs text-muted-foreground mt-1">Tip: use “|” to force a line break (e.g. "True North | Muslim Community").</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Mission Statement</label>
          <textarea value={form.mission_text} onChange={(e) => setForm({ ...form, mission_text: e.target.value })} rows={4} className="w-full px-3 py-2 rounded-lg bg-background border border-border" data-testid="homepage-mission" />
        </div>
        <Button type="submit" disabled={saving} data-testid="homepage-save-btn"><Save className="h-4 w-4 mr-2" />{saving ? 'Saving…' : 'Save Homepage'}</Button>
      </form>
    </div>
  );
};

// Fundraising Manager with Add/Subtract
const FundraisingManager = ({ token }) => {
  const [total, setTotal] = useState(0);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchTotal = async () => {
    try {
      const response = await axios.get(`${API}/fundraising/total`);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching total');
    }
  };

  useEffect(() => {
    fetchTotal();
  }, []);

  const handleAdd = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/fundraising/add`, 
        { amount: parseFloat(amount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Added $${amount}`);
      setAmount('');
      fetchTotal();
    } catch (error) {
      toast.error('Failed to add amount');
    } finally {
      setLoading(false);
    }
  };

  const handleSubtract = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/fundraising/subtract`, 
        { amount: parseFloat(amount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Subtracted $${amount}`);
      setAmount('');
      fetchTotal();
    } catch (error) {
      toast.error('Failed to subtract amount');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-foreground mb-6">Fundraising Management</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-6 rounded-xl bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/30">
          <h3 className="font-medium text-foreground mb-2">Current Manual Total</h3>
          <p className="text-5xl font-bold text-primary font-mono">${total.toLocaleString()}</p>
          <p className="text-sm text-muted-foreground mt-2">Cash & pledge donations</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={fetchTotal}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>

        <div className="p-6 rounded-xl bg-background border border-border">
          <h3 className="font-medium text-foreground mb-4">Adjust Amount</h3>
          <div className="flex gap-2 mb-4">
            <span className="flex items-center px-3 bg-muted rounded-l-xl border border-r-0 border-border">$</span>
            <Input
              type="number"
              placeholder="Enter amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="rounded-l-none"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAdd} disabled={loading} className="flex-1 bg-green-600 hover:bg-green-700">
              <Plus className="h-4 w-4 mr-2" /> Add
            </Button>
            <Button onClick={handleSubtract} disabled={loading} variant="destructive" className="flex-1">
              <Trash2 className="h-4 w-4 mr-2" /> Subtract
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <a href="/fundraising" target="_blank" className="inline-flex items-center gap-2 text-primary hover:underline">
          <Eye className="h-4 w-4" />
          View Fundraising Page
        </a>
      </div>
    </div>
  );
};

// Settings Manager
const SettingsManager = ({ token }) => {
  const [seeding, setSeeding] = useState(false);

  const handleSeed = async () => {
    if (!window.confirm('Import all default content (Events, Flyers, Board, Homepage)? This is safe to run multiple times — it only adds or updates the canonical defaults; it never deletes your custom content.')) return;
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/content/seed-defaults`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const c = res.data.counts || {};
      toast.success(`Imported defaults: ${c.events || 0} events, ${c.flyers || 0} flyers, ${c.board || 0} board members + homepage.`);
    } catch {
      toast.error('Seed failed — check backend logs.');
    }
    setSeeding(false);
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-foreground mb-6">Settings & Quick Links</h2>

      <div className="mb-6 p-6 rounded-xl bg-amber-500/10 border border-amber-500/30" data-testid="seed-defaults-card">
        <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-amber-500" />
          Import Default Content (one-time after deploy)
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Click this once on a fresh deployment to populate the database with the canonical Events, Flyers, Board Members, and Homepage text. Idempotent — re-running won't duplicate or delete anything custom.
        </p>
        <Button onClick={handleSeed} disabled={seeding} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="seed-defaults-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${seeding ? 'animate-spin' : ''}`} />
          {seeding ? 'Importing…' : 'Import Defaults Now'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-6 rounded-xl bg-background border border-border">
          <h3 className="font-medium text-foreground mb-4">Contact Information</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email:</span>
              <span className="text-foreground">truenorthmuslim@gmail.com</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Phone:</span>
              <span className="text-foreground">249-444-5227</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">E-Transfer:</span>
              <span className="text-foreground">truenorthmuslim@gmail.com</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Location:</span>
              <span className="text-foreground">54 St. Paul Crescent, Barrie</span>
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl bg-background border border-border">
          <h3 className="font-medium text-foreground mb-4">Code Files Reference</h3>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>• <code className="text-xs bg-muted px-1 rounded">FlyersPage.jsx</code> - Manage flyers</p>
            <p>• <code className="text-xs bg-muted px-1 rounded">EventsPage.jsx</code> - Manage events</p>
            <p>• <code className="text-xs bg-muted px-1 rounded">BoardPage.jsx</code> - Board members</p>
            <p>• <code className="text-xs bg-muted px-1 rounded">HomePage.jsx</code> - Homepage content</p>
            <p>• <code className="text-xs bg-muted px-1 rounded">EducationPage.jsx</code> - Education info</p>
          </div>
        </div>
      </div>

      <div className="mt-6 p-6 rounded-xl bg-background border border-border">
        <h3 className="font-medium text-foreground mb-4">Quick Links</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { href: '/', label: 'Home' },
            { href: '/gallery', label: 'Gallery' },
            { href: '/flyers', label: 'Flyers' },
            { href: '/events', label: 'Events' },
            { href: '/board', label: 'Board' },
            { href: '/education', label: 'Education' },
            { href: '/donate', label: 'Donate' },
            { href: '/fundraising', label: 'Fundraising' },
            { href: '/contact', label: 'Contact' },
          ].map((link) => (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm text-foreground transition-colors"
            >
              {link.label}
            </a>
          ))}
        </div>
      </div>

      <div className="mt-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30">
        <div className="flex items-center gap-2">
          <Check className="h-5 w-5 text-green-500" />
          <span className="text-sm text-foreground">Admin panel is working correctly</span>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
